import { useEffect, useRef } from 'react';
import ApexCharts from 'apexcharts';
import Tooltip from '@components/common/Tooltip';

const SizeMixedChart = () => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);

    useEffect(() => {
        if (!chartRef.current) return;

        // 최근 14일 날짜 생성
        const getLast14Days = () => {
            const dates = [];
            const today = new Date();
            for (let i = 13; i >= 0; i--) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                dates.push(`${date.getMonth() + 1}/${date.getDate()}`);
            }
            return dates;
        };

        // 생육 단계별 성장 데이터 생성
        const generateLinearGrowth = (
            initial: number,
            dailyIncrease: number,
            variance: number,
        ) => {
            const data = [];
            let current = initial;

            for (let i = 0; i < 14; i++) {
                // 성장 곡선: 초기 느림 -> 중기 빠름 -> 후기 느림
                const stageFactor = Math.sin((i / 14) * Math.PI) * 0.8 + 0.5;

                // 주기적 변동 추가 (3일 주기)
                const cyclicVariation = Math.sin((i / 3) * Math.PI) * 0.4;

                // 랜덤 변동
                const randomVar = (Math.random() - 0.5) * variance;

                // 가끔 성장 정체나 감소 (10% 확률)
                const stagnation =
                    Math.random() < 0.1 ? -dailyIncrease * 0.3 : 0;

                const growth =
                    dailyIncrease * (stageFactor + cyclicVariation) +
                    randomVar +
                    stagnation;
                current += growth;

                // 음수 방지
                current = Math.max(current, initial * 0.8);

                data.push(Math.round(current * 10) / 10);
            }
            return data;
        };

        const options = {
            series: [
                {
                    name: '높이',
                    type: 'column',
                    data: generateLinearGrowth(25, 2.5, 0.8), // 초기 25cm, 일일 2.5cm 증가
                },
                {
                    name: '너비',
                    type: 'column',
                    data: generateLinearGrowth(18, 1.8, 0.6), // 초기 18cm, 일일 1.8cm 증가
                },
                {
                    name: '뿌리깊이',
                    type: 'line',
                    data: generateLinearGrowth(15, 2.0, 0.5), // 초기 15cm, 일일 2.0cm 증가
                },
                {
                    name: '뿌리밀도',
                    type: 'line',
                    data: generateLinearGrowth(4.5, 0.3, 0.1), // 초기 4.5, 일일 0.3 증가
                },
            ],
            chart: {
                height: 320,
                type: 'line',
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: false,
                },
                dropShadow: {
                    enabled: true,
                    top: 3,
                    left: 0,
                    blur: 4,
                    color: '#000',
                    opacity: 0.1,
                },
            },
            stroke: {
                width: [0, 0, 3, 3],
                curve: 'smooth',
            },
            plotOptions: {
                bar: {
                    columnWidth: '50%',
                    borderRadius: 3,
                },
            },
            fill: {
                opacity: [0.85, 0.85, 1, 1],
                gradient: {
                    inverseColors: false,
                    shade: 'light',
                    type: 'vertical',
                    opacityFrom: 0.85,
                    opacityTo: 0.55,
                    stops: [0, 100, 100, 100],
                },
            },
            labels: getLast14Days(),
            markers: {
                size: [0, 0, 5, 5],
                colors: ['#FEB019', '#775DD0'],
                strokeColors: '#fff',
                strokeWidth: 2,
                hover: {
                    size: 7,
                },
            },
            xaxis: {
                labels: {
                    rotate: -45,
                },
            },
            yaxis: [
                {
                    title: {
                        text: '수치 (cm)',
                    },
                    labels: {
                        formatter: function (val: number) {
                            return val.toFixed(1) + ' cm';
                        },
                    },
                },
            ],
            colors: ['#00E396', '#008FFB', '#FEB019', '#775DD0'],
            legend: {
                show: true,
                position: 'top',
                markers: {
                    size: 5,
                    shape: 'circle',
                },
            },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 3,
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: function (val: number, opts: any) {
                        const seriesName =
                            opts.w.config.series[opts.seriesIndex].name;
                        if (seriesName === '뿌리밀도') {
                            return val.toFixed(1);
                        }
                        return val.toFixed(1) + ' cm';
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    return (
        <div className="custom-card card">
            <div className="card-header justify-content-between align-items-center d-sm-flex d-block">
                <div className="card-title mb-sm-0 mb-2">
                    생육크기정보
                    <Tooltip
                        content="도움말이 표시될예정"
                        customClass="tooltip-dark"
                        placement="top"
                    >
                        <i className="ri-question-line" />
                    </Tooltip>
                </div>
                <div
                    aria-label="Basic example"
                    role="group"
                    className="flex-wrap btn-group"
                >
                    <button type="button" className="btn-wave btn btn-success">
                        14일
                    </button>
                    <button
                        type="button"
                        className="btn-wave btn btn-success-light"
                    >
                        한달
                    </button>
                    <button
                        type="button"
                        className="btn-wave btn btn-success-light"
                    >
                        6개월
                    </button>
                    <button
                        type="button"
                        className="btn-wave btn btn-success-light"
                    >
                        1년
                    </button>
                    <button
                        type="button"
                        className="btn-wave btn btn-success-light"
                    >
                        더보기
                    </button>
                </div>
            </div>
            <div className="card-body">
                <div ref={chartRef} style={{}}></div>
            </div>
        </div>
    );
};

export default SizeMixedChart;
